// src/lib/kinema/core/effects/easing.ts
import type { EasingMode } from '../types';

export function applyEasing(
  mode: EasingMode,
  t: number,
  bezierPoints?: [number, number, number, number],
): number {
  switch (mode) {
    case 'linear':
      return t;
    case 'step':
      return t >= 1 ? 1 : 0;
    case 'ease-in':
      return t * t * t;
    case 'ease-out':
      return 1 - Math.pow(1 - t, 3);
    case 'ease-in-out':
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    case 'cubic-bezier':
      return bezierPoints
        ? cubicBezier(bezierPoints[0], bezierPoints[1], bezierPoints[2], bezierPoints[3], t)
        : t;
    default:
      return t;
  }
}

// Approximates cubic bezier — solves for y given x using Newton's method
function cubicBezier(x1: number, y1: number, x2: number, y2: number, t: number): number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;

  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  // Find parametric t for given x using Newton-Raphson
  let pt = t;
  for (let i = 0; i < 8; i++) {
    const x = ((ax * pt + bx) * pt + cx) * pt - t;
    if (Math.abs(x) < 1e-6) break;
    const dx = (3 * ax * pt + 2 * bx) * pt + cx;
    if (Math.abs(dx) < 1e-6) break;
    pt -= x / dx;
  }

  return ((ay * pt + by) * pt + cy) * pt;
}
